import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import AeoCallout from '../components/AeoCallout';
import RelatedBand from '../components/RelatedBand';
import { HERO_MEDIA } from '../config/media';
import {
    orgJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
} from '../lib/seo';

const PILLARS = [
    {
        head: 'Renewable material',
        body: 'Coastal Hem-Fir from BC\u2019s sustainably-managed coastal forests - a fast-cycle softwood with the tight grain and dimensional stability that gives Centurion mats their multi-deployment lifespan.',
    },
    {
        head: 'Field-level impact reduction',
        body: 'Mats spread concentrated equipment loads, reducing soil compaction and erosion, protecting underground utilities, and minimizing site disturbance in muskeg, wetlands, and Indigenous territory.',
    },
    {
        head: 'Multi-deployment lifecycle',
        body: 'Repairable across many deployments. End-of-life mats are recoverable for repurposing - the timber stays in the carbon cycle rather than going to single-use composite or steel landfill.',
    },
];

const FAQS = [
    {
        q: 'Where does Centurion\u2019s coastal Hem-Fir come from?',
        a: 'Sourced from coastal British Columbia under Canada\u2019s sustainable forestry standards, then milled at our Chemainus (Vancouver Island) facility. Tight, consistent grain - the structural advantage behind every Centurion mat.',
    },
    {
        q: 'How long does a Centurion access mat last?',
        a: 'Lifespan depends on duty cycle, ground conditions, and care between deployments, but a properly handled Centurion access mat survives many project deployments - coastal Hem-Fir Hi-Line #2 with the 177-bolt reinforced pattern is engineered specifically for repeated re-deploy.',
    },
    {
        q: 'Can Centurion mats be repaired and reused?',
        a: 'Yes. Bolt patterns are accessible for finger replacement and plank repair. Mats that come back from a project for off-hire are inspected, repaired where needed, and re-issued to the next campaign.',
    },
    {
        q: 'What happens to mats at end-of-life?',
        a: 'End-of-life mats can be repurposed (e.g.\u00A0secondary infill, low-traffic ground protection) before timber is recovered. This is in contrast to single-use composite or steel ground protection, which carries higher embodied carbon and limited end-of-life options.',
    },
];

export default function Sustainability() {
    return (
        <>
            <SEO
                title="Sustainability - Renewable Timber Matting"
                description={'Centurion timber matting is renewable, repairable, and reusable. Coastal Hem-Fir from BC\u2019s sustainably-managed forests, engineered for multi-deployment lifespans across access, rig and crane mat applications.'}
                path="/partnerships/sustainability"
                image={HERO_MEDIA.sustainableForestry.src}
                jsonLd={[
                    orgJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Partnerships', path: '/partnerships' },
                        { name: 'Sustainability', path: '/partnerships/sustainability' },
                    ]),
                    faqJsonLd(FAQS),
                    {
                        '@context': 'https://schema.org',
                        '@type': 'AboutPage',
                        name: 'Centurion Matting Sustainability',
                        url: 'https://centurionmatting.com/partnerships/sustainability',
                    },
                ]}
            />

            <HeroPlaceholder
                mediaKey="sustainableForestry"
                eyebrow="Sustainability"
                title="Renewable timber. Reduced ground impact. Long-lifecycle mats."
                subtitle="Timber matting is a renewable, repairable, and reusable ground-protection technology - engineered to deliver multi-deployment lifespans across project after project."
            />

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                <p className="mt-8 font-sans text-lg leading-relaxed text-brand-text">
                    Timber matting is a renewable, repairable, and reusable ground-protection technology. Centurion access, rig, and crane mats are manufactured from coastal Hem-Fir - a fast-growing, sustainably-managed Canadian softwood - and engineered to deliver multi-deployment lifespans across project after project.
                </p>
                <p className="mt-6 font-sans text-base leading-relaxed text-brand-muted">
                    Beyond the renewable material story, our mats deliver direct environmental benefits in the field: stabilizing terrain to reduce soil compaction and erosion, protecting underground utilities, and minimizing site disturbance in sensitive ecosystems including muskeg, wetlands, and Indigenous territory.
                </p>
            </section>

            {/* Three pillars */}
            <section className="border-y border-brand-hairline bg-brand-surface" data-eyebrow-tone="blue">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">Sustainability Pillars</span>
                    <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                        Three pillars, one engineered system.
                    </h2>
                    <div className="mt-12 grid gap-px bg-brand-hairline md:grid-cols-3" data-testid="sus-pillars">
                        {PILLARS.map((p, i) => (
                            <div key={p.head} className="bg-brand-surface p-6 md:p-8">
                                <span className="font-display text-xs font-bold tabular-nums tracking-[0.2em] text-brand-gold">
                                    0{i + 1}
                                </span>
                                <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-brand-text">
                                    {p.head}
                                </h3>
                                <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* AEO callout */}
            <section className="mx-auto max-w-4xl px-6 py-20 md:py-24">
                <AeoCallout
                    testId="aeo-sus"
                    q="Why is timber matting more sustainable than alternatives?"
                    a="Coastal Hem-Fir is a fast-cycle renewable resource. Timber mats are repairable across many deployments and can be recovered or repurposed at end-of-life. Compared to the embodied carbon of single-use composite or steel ground protection, timber matting offers a substantially lower lifecycle footprint."
                />
            </section>

            {/* Indigenous tie-in */}
            <section className="border-y border-brand-hairline bg-brand-surface">
                <div className="mx-auto max-w-7xl grid gap-12 px-6 py-20 md:grid-cols-[1fr_1.2fr] md:px-10 md:py-24 md:gap-16">
                    <div>
                        <span className="eyebrow">Aligned with Indigenous Partnerships</span>
                        <h2 className="h-display mt-6 text-2xl sm:text-3xl">
                            Sovereignty over the land. Participation in the supply.
                        </h2>
                    </div>
                    <div>
                        <p className="font-sans text-base leading-relaxed text-brand-muted">
                            Our sustainability commitments work in alignment with our Indigenous Partnership Program - respecting Indigenous sovereignty over the lands our mats operate on, and supporting First Nations participation in the matting supply chain through our partner, Bear Valley Solutions.
                        </p>
                        <Link
                            to="/partnerships/indigenous"
                            data-testid="sus-indigenous-link"
                            className="mt-6 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.18em] text-brand-gold hover:text-brand-gold-hover"
                        >
                            Indigenous Partnerships
                            <ArrowRight size={12} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">FAQ</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Common questions about timber-mat sustainability.
                </h2>
                <div className="mt-10">
                    <FaqAccordion faqs={FAQS} testId="faq-sus" />
                </div>
            </section>

            <RelatedBand
                heading="Adjacent partnerships."
                items={[
                    { to: '/partnerships/indigenous', eyebrow: 'Indigenous', title: 'Indigenous Partnerships', body: 'First Nations employment, procurement, and joint-venture frameworks through our partner, Bear Valley Solutions.' },
                    { to: '/partnerships/procurement', eyebrow: 'Procurement', title: 'Procurement Partnerships', body: 'Manufacturer-direct wholesale for contractors and matting service companies.' },
                    { to: '/projects', eyebrow: 'Projects', title: 'All Projects', body: 'Pipeline, infrastructure, wind, and partnerships in one view.' },
                ]}
            />

            <CtaBand
                eyebrow="Partnership Inquiries"
                title="Talk to us about partnership and sustainability."
                body="Ground protection that respects the land, the community, and the project."
                cta={{ to: '/contact?topic=partnership', label: 'Contact for Partnership' }}
            />
        </>
    );
}
